import { css } from "styled-system/css";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { cf } from "@/components/brand/sfturnos-styles";
import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { DashboardEmpty } from "@/components/dashboard/dashboard-empty";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { useToast } from "@/hooks/useToast";
import { Button } from "@/components/ui/button";
import { CalendarDays, Mail, Phone } from "lucide-react";

type Request = {
  id: string;
  clientName: string;
  clientEmail: string | null;
  clientPhone: string | null;
  notes: string | null;
  serviceName: string;
  serviceDuration: number;
  createdAt: string;
};

function ScheduleModal({
  req,
  onClose,
  onConfirmed,
}: {
  req: Request;
  onClose: () => void;
  onConfirmed: () => void;
}) {
  const { refresh } = useAuth();
  const toast = useToast();
  const [startTime, setStartTime] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    if (!startTime) {
      setError("Seleccioná fecha y hora.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const startTimeIso = new Date(startTime).toISOString();
      const r = await fetchWithSessionSync(
        `/api/dashboard/appointments/${req.id}/confirm-request`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ startTimeIso }),
        },
        refresh,
      );
      const data = await r.json();
      if (!r.ok) throw new Error(data.error ?? "Error");
      toast.success("Turno confirmado");
      onConfirmed();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className={css({
        position: "fixed",
        inset: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: "4",
        bg: "rgba(15, 23, 42, 0.5)",
      })}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className={css({
          bg: "white",
          rounded: "xl",
          p: "6",
          w: "full",
          maxW: "420px",
          boxShadow: "0 20px 60px rgba(15, 23, 42, 0.2)",
        })}
      >
        <h3 className={css({ fontSize: "lg", fontWeight: "bold", color: "#0f172a", mb: "1" })}>
          Programar turno
        </h3>
        <p className={css({ fontSize: "sm", color: "#64748b", mb: "4" })}>
          {req.clientName} · {req.serviceName} ({req.serviceDuration} min)
        </p>

        <label className={css({ display: "flex", flexDir: "column", gap: "1", mb: "4" })}>
          <span className={css({ fontSize: "sm", color: "#334155", fontWeight: "medium" })}>Fecha y hora</span>
          <input
            type="datetime-local"
            className={cf.input}
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            min={new Date().toISOString().slice(0, 16)}
          />
        </label>

        {error ? (
          <p className={css({ fontSize: "sm", color: "red.600", mb: "3" })}>{error}</p>
        ) : null}

        <div className={css({ display: "flex", gap: "2", justifyContent: "flex-end" })}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="button" variant="primary" disabled={saving} onClick={() => void confirm()}>
            {saving ? "Guardando…" : "Confirmar turno"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function DashboardRequests() {
  const { refresh } = useAuth();
  const [requests, setRequests] = useState<Request[] | null>(null);
  const [scheduling, setScheduling] = useState<Request | null>(null);

  const load = useCallback(async () => {
    const r = await fetchWithSessionSync("/api/dashboard/appointment-requests", { credentials: "include" }, refresh);
    if (!r.ok) return;
    const data = await r.json() as { requests: Request[] };
    setRequests(data.requests);
  }, [refresh]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <DashboardPageShell
      eyebrow="Gestión"
      title="Solicitudes de turno"
      description="Clientes que pidieron un turno. Coordiná con ellos y asignales fecha y hora."
    >
      {requests === null ? (
        <LoadingFrame variant="light" minHeight={280} />
      ) : requests.length === 0 ? (
        <DashboardEmpty
          title="Sin solicitudes pendientes"
          description="Cuando un cliente solicite un turno desde tu link público, aparecerá aquí."
        />
      ) : (
        <div className={css({ display: "flex", flexDir: "column", gap: "3" })}>
          {requests.map((req) => (
            <div
              key={req.id}
              className={css({
                bg: "white",
                rounded: "xl",
                borderWidth: "1px",
                borderColor: "#e2e8f0",
                p: "5",
                display: "flex",
                flexDir: { base: "column", sm: "row" },
                alignItems: { sm: "center" },
                gap: "4",
                boxShadow: "0 2px 8px rgba(15,23,42,0.06)",
              })}
            >
              <div className={css({ flex: "1", minW: 0 })}>
                <div className={css({ display: "flex", alignItems: "center", gap: "2", mb: "1" })}>
                  <p className={css({ fontWeight: "semibold", fontSize: "sm", color: "#0f172a", truncate: true })}>
                    {req.clientName}
                  </p>
                  <span className={css({ fontSize: "xs", color: "#64748b", bg: "#f1f5f9", px: "2", py: "0.5", rounded: "full", flexShrink: 0 })}>
                    {req.serviceName}
                  </span>
                </div>
                <div className={css({ display: "flex", gap: "3", flexWrap: "wrap", mt: "1" })}>
                  {req.clientEmail ? (
                    <a
                      href={`mailto:${req.clientEmail}`}
                      className={css({ display: "flex", alignItems: "center", gap: "1", fontSize: "xs", color: "#4f46e5" })}
                    >
                      <Mail size={13} aria-hidden />
                      {req.clientEmail}
                    </a>
                  ) : null}
                  {req.clientPhone ? (
                    <a
                      href={`https://wa.me/${req.clientPhone.replace(/\D/g, "")}`}
                      target="_blank"
                      rel="noreferrer"
                      className={css({ display: "flex", alignItems: "center", gap: "1", fontSize: "xs", color: "#16a34a" })}
                    >
                      <Phone size={13} aria-hidden />
                      {req.clientPhone}
                    </a>
                  ) : null}
                </div>
                {req.notes ? (
                  <p className={css({ fontSize: "xs", color: "#64748b", mt: "2", fontStyle: "italic", lineHeight: "1.5" })}>
                    "{req.notes}"
                  </p>
                ) : null}
                <p className={css({ fontSize: "xs", color: "#94a3b8", mt: "2" })}>
                  Solicitado {format(new Date(req.createdAt), "d MMM yyyy 'a las' HH:mm", { locale: es })}
                </p>
              </div>
              <Button
                type="button"
                variant="primary"
                onClick={() => setScheduling(req)}
                className={css({ flexShrink: 0 })}
              >
                <CalendarDays size={16} aria-hidden />
                Programar turno
              </Button>
            </div>
          ))}
        </div>
      )}

      {scheduling ? (
        <ScheduleModal
          req={scheduling}
          onClose={() => setScheduling(null)}
          onConfirmed={() => {
            setScheduling(null);
            void load();
          }}
        />
      ) : null}
    </DashboardPageShell>
  );
}
